<template>
  <div id="app" class="sgstyle">
    <nprogress-container />

    <transition name="slideIn">
      <sidebar
        v-if="publicLayout.status === false && show"
        :show.sync="sidebar.opened"
      />
    </transition>

    <app-main v-if="show" />
  </div>
</template>

<script>
  import { getToken } from "utils/auth";
  import NprogressContainer from "vue-nprogress/src/NprogressContainer";
  import { mapGetters, mapActions } from "vuex";
  import { eBus } from "utils/eventbus";
  import mixin from "components/mixin";
  import { user, setting } from "api";

  export default {
    components: {
      "app-main": () => import("../components/layout/app-main"),
      "sidebar": () => import("../components/layout/sidebar"),
      NprogressContainer,
    },
    mixins: [mixin],
    data() {
      return {
        show: false,
      };
    },
    computed: mapGetters({
      permissions: "permissions",
      sidebar: "sidebar",
      publicLayout: "publicLayout",
    }),
    created() {
      eBus.$on("unauthorized", () => {
        this.logout();
      });
      eBus.$on("login", async () => {
        await this.getInfo();
        await this.getAllSetting();
        await this.getCurrency();
        await this.$router.push({ path: "/dashboard" });
      });
      eBus.$on("403", (err) => {
        this.$router.push({ name: "403", params: { err } });
      });
    },
    beforeMount() {
      this.setHistoryLength();
      const { body } = document;
      const WIDTH = 768;
      const RATIO = 3;

      const handler = () => {
        if (!document.hidden) {
          let rect = body.getBoundingClientRect();
          let isMobile = rect.width - RATIO < WIDTH;
          this.toggleDevice(isMobile ? "mobile" : "other");
          this.toggleSidebar(!isMobile);
        }
      };

      document.addEventListener("visibilitychange", handler);
      window.addEventListener("DOMContentLoaded", handler);
      window.addEventListener("resize", handler);
    },
    mounted() {
      if (getToken()) {
        this.getInfo();
        this.getAllSetting();
        this.getCurrency();
      } else {
        this.show = true;
      }
    },
    methods: {
      ...mapActions(["toggleDevice", "toggleSidebar"]),
      async getInfo() {
        try {
          const { data } = await user.find();
          const permissions = data.data.available_permissions;
          const { available_permissions } = data.data;
          const { available_roles } = data.data;
          const profile = {
            available_roles,
            available_permissions,
            ...data.data,
          };
          await this.$store.dispatch("user/setPermissions", permissions);
          await this.$store.dispatch("profile", profile);
        } catch (error) {
          if (error.response) {
            this.openNotificationWithType("danger", {
              title: "Failed!",
              message: error.response.data.error.message,
            });
          }
        }
      },
      getAllSetting() {
        this.show = false;
        let loader = this.$loading.show();
        let params = {
          format_by_key: "1",
        };
        setting
          .getAllSettingPublic(params)
          .then((res) => {
            if (res.data.success) {
              let timezone = res.data.data.admin_timezone
                ? res.data.data.admin_timezone
                : null;
              let isGMTFeature = res.data.data.gmt_format_feature;
              timezone = isGMTFeature === 0 ? null : timezone;
              this.$store.commit("TIMEZONE", timezone);
              this.$store.commit("SETTING", res.data.data);
              this.show = true;
              loader.hide();
            }
          })
          .catch((error) => {
            console.log(error);
          });
      },
      getCurrency() {
        let loader = this.$loading.show();
        setting
          .getCurrency()
          .then((res) => {
            if (res.data.success) {
              let symbol = res.data.data;
              this.$store.commit("CURRENCY", symbol);
            }
            loader.hide();
          })
          .catch(() => {
            loader.hide();
          });
      },
      setHistoryLength() {
        this.$root.historyCount = window.history.length;
      },
    },
  };
</script>

<style lang="scss">
  @import "~animate.css";

  .animated {
    animation-duration: 0.377s;
  }

  @import "../css/custom";
  $fa-font-path: "~font-awesome/fonts/";
  @import "~font-awesome/scss/font-awesome";

  html {
    background-color: whitesmoke;
    font-size: 14px;
    overflow-y: auto;
  }

  .app-main {
    min-height: calc(90vh - 10rem);
  }

  .pac-container {
    z-index: 9999999;
  }

  .gmap .control.is-horizontal > .control {
    flex-grow: 3;
  }

  .gmap-left .or {
    float: left;
    width: 5%;
  }

  .gmap-left .get-location {
    float: left;
    width: 95%;
    text-align: center;
    padding: 20px 0px;
  }

  .or {
    display: inline-block;
    height: 100%;
    position: relative;
    width: 20px;
    text-align: center;
    vertical-align: middle;
    float: left;
  }

  .or:before {
    content: "";
    position: absolute;
    display: block;
    height: calc(50% - 15px);
    width: 1px;
    top: 0;
    left: 50%;
    background-color: rgb(114, 114, 114);
  }

  .or span {
    position: absolute;
    height: 20px;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
  }

  .or:after {
    content: "";
    position: absolute;
    display: block;
    height: calc(50% - 15px);
    width: 1px;
    bottom: 0;
    left: 50%;
    background-color: rgb(114, 114, 114);
  }

  .nprogress-container {
    position: fixed !important;
    width: 100%;
    height: 50px;
    z-index: 2048;
    pointer-events: none;

    #nprogress {
      $color: #48e79a;

      .bar {
        background: $color;
      }
      .peg {
        box-shadow: 0 0 10px $color, 0 0 5px $color;
      }

      .or span {
        position: absolute;
        height: 20px;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
      }

      .or:after {
        content: "";
        position: absolute;
        display: block;
        height: calc(50% - 15px);
        width: 1px;
        bottom: 0;
        left: 50%;
        background-color: rgb(114, 114, 114);
      }

      .nprogress-container {
        position: fixed !important;
        width: 100%;
        height: 50px;
        z-index: 2048;
        pointer-events: none;

        #nprogress {
          $color: #48e79a;

          .bar {
            background: $color;
          }
          .peg {
            box-shadow: 0 0 10px $color, 0 0 5px $color;
          }

          .spinner-icon {
            border-top-color: $color;
            border-left-color: $color;
          }
        }
      }
    }
  }

  .slideIn-enter,
  .slideIn-leave-to {
    transition: translateX(30px);
    opacity: 0;
  }

  .slideIn-enter-active,
  .slideIn-leave-active {
    transition: all 0.9s;
  }
</style>
